import './App.css';
import { useEffect, useState } from 'react';
import { FormsProvider } from './context/FormsContext';
import Navbar from './components/ckr/Navbar';
import Hero from './components/ckr/Hero';
import Motivation from './components/ckr/Motivation';
import MarketIndex from './components/ckr/MarketIndex';
import Testimonials from './components/ckr/Testimonials';
import AboutMe from './components/ckr/AboutMe';
import NeedAnalysisForms from './components/ckr/NeedAnalysisForms';
import ConsultancyFlow from './components/ckr/ConsultancyFlow';
import ApproachClosing from './components/ckr/ApproachClosing';
import FeedbackForm from './components/ckr/FeedbackForm';
import MelisAssistant from './components/ckr/MelisAssistant';
import Footer from './components/ckr/Footer';
import AdminPanel from './components/ckr/AdminPanel';
import BlogCenter from './components/ckr/BlogCenter';
import BlogArticle from './components/ckr/BlogArticle';
import {blogCategoryFromSlug} from './lib/blog';
import {applyPageMetadata} from './lib/seo';
import {NewsletterManage} from './components/ckr/NewsletterSignup';
import { contentPages, normalizePath, sectionPages, navigateTo } from './lib/navigation';
import { applyContentMetadata } from './lib/pageMetadata';

const HARBOR = '/media/harbor.webp';
const COOKIE_PREF_KEY = 'ckr_cookie_preferences_v1';

function isAdminRoute() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/admin' || window.location.hash === '#admin';
}

function CookiePreferenceNotice() {
  const [visible, setVisible] = useState(false);
  const [preferences, setPreferences] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(COOKIE_PREF_KEY);
      if (!stored) setVisible(true);
      if (stored) {
        const parsed = JSON.parse(stored);
        setPreferences({
          analytics: !!parsed.analytics,
          marketing: !!parsed.marketing,
        });
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const save = (nextPreferences) => {
    const payload = {
      necessary: true,
      analytics: !!nextPreferences.analytics,
      marketing: !!nextPreferences.marketing,
      saved_at: new Date().toISOString(),
      version: '2026-10-12',
    };
    try {
      window.localStorage.setItem(COOKIE_PREF_KEY, JSON.stringify(payload));
    } catch {
      // Preference storage can fail in private browsing; the choice still applies for this view.
    }
    window.dispatchEvent(new CustomEvent('ckr-cookie-preferences-updated', { detail: payload }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="ckr-cookie-banner" role="dialog" aria-label="Çerez tercihleri">
      <div>
        <strong>Çerez tercihleri</strong>
        <p>
          Zorunlu teknik çerezler ve oturum saklama alanları dışında analitik, reklam veya pazarlama çerezleri varsayılan olarak etkin değildir.
          İsterseniz bu kategorilere ayrı ayrı izin verebilirsiniz.
        </p>
        <span>Zorunlu teknik çerezler: her zaman aktif</span>
      </div>
      <label>
        <input
          type="checkbox"
          checked={preferences.analytics}
          onChange={(event) => setPreferences((current) => ({ ...current, analytics: event.target.checked }))}
        />
        Analitik çerezlere izin ver
      </label>
      <label>
        <input
          type="checkbox"
          checked={preferences.marketing}
          onChange={(event) => setPreferences((current) => ({ ...current, marketing: event.target.checked }))}
        />
        Reklam ve pazarlama çerezlerine izin ver
      </label>
      <div className="ckr-cookie-actions">
        <button type="button" className="ckr-btn ckr-btn-ghost" onClick={() => save({ analytics: false, marketing: false })}>
          Yalnızca Zorunlu
        </button>
        <button type="button" className="ckr-btn ckr-btn-gold" onClick={() => save(preferences)}>
          Tercihleri Kaydet
        </button>
      </div>
    </div>
  );
}

function AppContent({ initialPost = null } = {}) {
  const [isAdmin, setIsAdmin] = useState(isAdminRoute);
  const [routePath, setRoutePath] = useState(() => window.location.pathname);

  useEffect(() => {
    const syncRoute = () => { setIsAdmin(isAdminRoute()); setRoutePath(window.location.pathname); };
    window.addEventListener('hashchange', syncRoute);
    window.addEventListener('popstate', syncRoute);
    return () => {
      window.removeEventListener('hashchange', syncRoute);
      window.removeEventListener('popstate', syncRoute);
    };
  }, []);

  useEffect(() => {
    const path = normalizePath(routePath);
    if (path === '/' && window.location.hash !== '#admin') {
      const legacy = sectionPages[window.location.hash];
      if (legacy && !['/', '/#piyasa'].includes(legacy)) {
        const target = new URL(legacy, window.location.origin);
        target.search = window.location.search;
        navigateTo(target.pathname + target.search + target.hash, {replace: true});
      }
      else if (new URLSearchParams(window.location.search).has('form')) navigateTo('/ihtiyac-analizi/' + window.location.search, {replace: true});
    }
    if (contentPages[path] || path === '/') applyContentMetadata(path);
    if(path==='/blog/' || /^\/blog\/sayfa\/\d+\/$/.test(path))applyPageMetadata(null,true,Number(path.split('/')[3])||1);
    const categoryMatch=path.match(/^\/blog\/kategori\/([^/]+)\/(?:sayfa\/(\d+)\/)?$/);
    if(categoryMatch && blogCategoryFromSlug(categoryMatch[1]))applyPageMetadata(null,true,Number(categoryMatch[2])||1,blogCategoryFromSlug(categoryMatch[1]));
  }, [routePath]);

  if (routePath.replace(/\/+$/, '') === '/bulten') return <NewsletterManage/>;

  if (isAdmin) {
    return <AdminPanel />;
  }

  if (/^\/blog(?:\/|$)/.test(routePath)) {
    const parts = routePath.split('/').filter(Boolean);
    const isCategory=parts[1]==='kategori';
    const category=isCategory ? blogCategoryFromSlug(parts[2]) : 'Tümü';
    const isPage=parts[1]==='sayfa';
    const page=isCategory ? Math.max(1,Number(parts[4])||1) : isPage ? Math.max(1,Number(parts[2])||1) : 1;
    const slug=!isPage&&!isCategory ? parts[1] : null;
    return <div className="App ckr-blog-page" style={{ backgroundImage: `url(${HARBOR})`, backgroundSize: 'cover', backgroundAttachment: 'fixed' }}><Navbar /><main>{slug ? <BlogArticle key={slug} slug={slug} initialPost={initialPost}/> : <BlogCenter key={`${category}-${page}`} initialPage={page} initialCategory={category || 'Tümü'}/>}</main><Footer compact /><MelisAssistant /><CookiePreferenceNotice /></div>;
  }

  const path = normalizePath(routePath);
  const isHome = path === '/';
  const page = contentPages[path];
  let content = null;
  if (path === '/ihtiyac-analizi/') content = <NeedAnalysisForms />;
  if (path === '/motivasyon/') content = <Motivation />;
  if (path === '/ben-kimim/') content = <AboutMe />;
  if (path === '/danismanlik/') content = <><ConsultancyFlow /><ApproachClosing /></>;
  if (path === '/iletisim/') content = <><Footer /><FeedbackForm /></>;

  return <div className={`App${isHome ? '' : ' ckr-content-page'}`}>
    <div className="ckr-page-background" style={{ backgroundImage: `url(${HARBOR})` }} />
    <Navbar />
    <main id={isHome ? undefined : 'top'} className={isHome ? 'ckr-home-main' : 'ckr-page-main'}>
      {isHome ? <><Hero /><div className="ckr-glass-column"><Testimonials /><MarketIndex /></div></> :
        <div className="ckr-glass-column ckr-page-column">
          <div className="ckr-page-crumb"><a href="/" onClick={e => { e.preventDefault(); navigateTo('/'); }}>← Ana sayfa</a><span>{page?.title || 'Sayfa bulunamadı'}</span></div>
          {path !== '/ben-kimim/' && <h1 className="ckr-page-title">{page?.title || 'Sayfa bulunamadı'}</h1>}
          {content || <p className="ckr-page-empty">Bu sayfa bulunamadı. Üst menüden devam edebilirsiniz.</p>}
        </div>}
    </main>
    <Footer compact />
    <MelisAssistant />
    <CookiePreferenceNotice />
  </div>;
}

function App(props) {
  return <FormsProvider><AppContent {...props} /></FormsProvider>;
}
export default App;
